import { Alert } from 'src/design/atoms/Alert';
import { ColorSwatch } from 'src/design/atoms/ColorSwatch';
import { Group } from 'src/design/atoms/Group';
import { Loader } from 'src/design/atoms/Loader';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { BaseModal } from 'src/design/molecules/BaseModal';
import { Select } from 'src/design/atoms/Select';
import { IconAlertCircle } from '@tabler/icons-react';
import React from 'react';
import { EffectiveRateBreakdown } from 'src/app/timesheets/EffectiveRateBreakdown';
import { useEffectiveRatePreview } from 'src/hooks/useEffectiveRatePreview';
import { useJobs } from 'src/hooks/useJobs';
import { useOrganization } from 'src/hooks/useOrganization';
import { useUsersList } from 'src/api/queries';

interface PayCalculatorModalProps {
  opened: boolean;
  onClose: () => void;
}

export const PayCalculatorModal: React.FC<PayCalculatorModalProps> = ({
  opened,
  onClose,
}) => {
  const { data: users = [] } = useUsersList();
  const { jobs } = useJobs();
  const { payCodes } = useOrganization();

  const [userId, setUserId] = React.useState<number | null>(null);
  const [jobId, setJobId] = React.useState<number | null>(null);
  const [payCodeId, setPayCodeId] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!opened) {
      setUserId(null);
      setJobId(null);
      setPayCodeId(null);
    }
  }, [opened]);

  const { instructions, loading, error } = useEffectiveRatePreview({
    userId: userId ?? 0,
    jobId: jobId ?? 0,
    payCodeId: payCodeId ?? '',
  });

  const userOptions = React.useMemo(
    () =>
      users
        .filter((u) => !u.isDisabled)
        .map((u) => ({
          value: String(u.id),
          label: u.employeeId ? `${u.fullName} (${u.employeeId})` : u.fullName,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [users],
  );

  const jobOptions = React.useMemo(
    () =>
      jobs
        .filter((j) => !j.archived)
        .map((j) => ({
          value: String(j.id),
          label: j.jobNumber ? `${j.jobNumber} — ${j.name}` : j.name ?? '',
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [jobs],
  );

  const selectedPayCode = React.useMemo(
    () => payCodes.find((pc) => pc.id === payCodeId),
    [payCodes, payCodeId],
  );

  const payCodeOptions = React.useMemo(
    () =>
      payCodes
        .map((pc) => ({ value: pc.id, label: pc.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [payCodes],
  );

  const allSelected = !!userId && !!jobId && !!payCodeId;

  const renderResult = () => {
    if (!allSelected) {
      return (
        <Text c="dimmed" size="sm">
          Select a user, job, and pay code to calculate the effective pay rate.
        </Text>
      );
    }
    if (loading) {
      return (
        <Group justify="center" py="lg">
          <Loader />
        </Group>
      );
    }
    if (error) {
      return (
        <Alert
          color="red"
          icon={<IconAlertCircle size={18} />}
          title="Unable to calculate rate"
        >
          {error}
        </Alert>
      );
    }
    if (instructions) {
      return <EffectiveRateBreakdown instructions={instructions} />;
    }
    return null;
  };

  return (
    <BaseModal
      opened={opened}
      onClose={onClose}
      title="Pay Calculator"
      size="xl"
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Reproduce the exact pay calculation a timesheet would produce for any
          combination of user, job, and pay code.
        </Text>

        <Group grow align="flex-start">
          <Select
            label="User"
            placeholder="Select user"
            searchable
            data={userOptions}
            value={userId != null ? String(userId) : null}
            onChange={(v) => setUserId(v ? Number(v) : null)}
            nothingFoundMessage="No users"
          />
          <Select
            label="Job"
            placeholder="Select job"
            searchable
            data={jobOptions}
            value={jobId != null ? String(jobId) : null}
            onChange={(v) => setJobId(v ? Number(v) : null)}
            nothingFoundMessage="No jobs"
          />
          <Select
            label="Pay Code"
            placeholder="Select pay code"
            searchable
            data={payCodeOptions}
            value={payCodeId}
            onChange={setPayCodeId}
            nothingFoundMessage="No pay codes"
            leftSection={
              selectedPayCode?.color ? (
                <ColorSwatch color={selectedPayCode.color} size={14} />
              ) : undefined
            }
          />
        </Group>

        {renderResult()}
      </Stack>
    </BaseModal>
  );
};
